import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { Listbox } from '@headlessui/react';
import useApi from '../../hooks/use-api';
import DropzoneWrapper from '../../components/Dropzone/Dropzone'
import Sidebar from '../../components/Sidebar/Sidebar'
import { SidebarItemProps } from '../../components/Sidebar/SidebarItem'
import AccountTopBar from '../../components/AccountTopBar/AccountTopBar'
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog'
import { Tags, Tag } from '../../utils/props';
import { documentSections, documentTagNames } from '../../utils/documentSections';
import { buildAuthenticatedFileUrl } from '../../utils/utils';

export default function DocumentsPage() {
  const { t } = useTranslation('ns');
  const [userProfileTag, setUserProfileTag] = useState<number | undefined>(undefined);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [tagIds, setTagIds] = useState<Record<string, number>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<number, Array<{ id: number; name: string, url: string, expirationDate: string, notes: string }>>>({});
  const [selectedSectionId, setSelectedSectionId] = useState(documentSections[0].id);
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<{ expirationDate: string, notes: string }>({ expirationDate: '', notes: '' });
  const [pendingFiles, setPendingFiles] = useState<Record<number, Array<{ key: string; file: File; expirationDate: string; notes: string }>>>({});
  const [savingPendingKey, setSavingPendingKey] = useState<string | null>(null);
  const [fileIdPendingDelete, setFileIdPendingDelete] = useState<{ id: number; tag: number } | null>(null);
  const syncingTagsRef = useRef(false);
  const syncedCompaniesRef = useRef<Set<number>>(new Set());

  const hasPendingFiles = useMemo(
    () => Object.values(pendingFiles).some((entries) => entries.length > 0),
    [pendingFiles],
  );

  // Warn on tab close / refresh / typing a new address while files are staged but unsaved.
  useEffect(() => {
    if (!hasPendingFiles) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPendingFiles]);

  // Warn on the in-app browser Back/Forward button while files are staged but unsaved.
  useEffect(() => {
    if (!hasPendingFiles) {
      return;
    }

    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      const confirmLeave = window.confirm('You have unsaved file uploads. Are you sure you want to leave without saving?');
      if (confirmLeave) {
        window.removeEventListener('popstate', handlePopState);
        window.history.back();
      } else {
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasPendingFiles]);

  const getFileDisplayName = (value: string) => {
    const rawValue = (value || '').trim();
    if (!rawValue) return '';

    try {
      if (/^https?:\/\//i.test(rawValue)) {
        const parsed = new URL(rawValue);
        const segments = parsed.pathname.split('/').filter(Boolean);
        return segments[segments.length - 1] || rawValue;
      }
    } catch {
      // Fall back to path split below.
    }

    const normalized = rawValue.replace(/\\/g, '/');
    const segments = normalized.split('/').filter(Boolean);
    return segments[segments.length - 1] || rawValue;
  }

  const getUser = useApi({
    path: '/user',
    callback: (data) => {
      const resolvedCompanyId = Number(data.id);
      localStorage.setItem('user_id', JSON.stringify(data.id));
      localStorage.setItem('first_name', JSON.stringify(data.first_name));
      localStorage.setItem('last_name', JSON.stringify(data.last_name));
      localStorage.setItem('company_name', JSON.stringify(data.company_name));
      localStorage.setItem('phone', JSON.stringify(data.phone));
      localStorage.setItem('mail', JSON.stringify(data.mail));
      localStorage.setItem('date_of_birth', JSON.stringify(data.date_of_birth));
      localStorage.setItem('type', JSON.stringify(data.type));
      setCompanyId(Number.isNaN(resolvedCompanyId) ? null : resolvedCompanyId);
    }
  })

  const fetchFilesApi = useApi({
    path: '',
    callback: (data, context) => {
      const tagId = context?.tagId;
      if (data && data.items && tagId) {
        setUploadedFiles(prev => ({
          ...prev,
          [tagId]: data.items.map((file: any) => ({
            id: file.id,
            name: getFileDisplayName(file.title || file.filename),
            url: file.filename,
            expirationDate: file.expiration_date || '',
            notes: file.notes || '',
          })),
        }));
      }
    }
  });

  const postTagApi = useApi({
    path: '/tags',
  });

  const tagsApi = useApi({
    path: '/tags',
  });

  const buildTagState = (allTags: Tag[]) => {
    const foundTagIds: Record<string, number> = {};
    const tagNames: string[] = [String(Tags.ProfilePicture), ...documentTagNames];

    tagNames.forEach((tagName) => {
      const found = allTags.find((item) => item.name === tagName && item.deleted !== 1);
      if (found) {
        foundTagIds[tagName] = found.id;
      }
    });

    return foundTagIds;
  };

  const getMissingTagNames = (tagState: Record<string, number>) => {
    const tagNames: string[] = [String(Tags.ProfilePicture), ...documentTagNames];
    return tagNames.filter((tagName) => typeof tagState[tagName] !== 'number');
  };

  const fetchAllCompanyTags = async (resolvedCompanyId: number): Promise<Tag[]> => {
    const allTags: Tag[] = [];
    let currentPage = 1;
    let totalPages = 1;

    while (currentPage <= totalPages) {
      const response = await tagsApi.get(
        `?company_id=${resolvedCompanyId}&current_page=${currentPage}&items_per_page=500`,
      );
      const payload = response?.data;
      const items: Tag[] = payload?.items ?? [];
      const nextTotalPages = Number(payload?.total_pages ?? 1);

      allTags.push(...items);
      totalPages = Number.isNaN(nextTotalPages) ? 1 : nextTotalPages;
      currentPage += 1;
    }

    return allTags;
  };

  useEffect(() => {
    getUser.get();
  }, [])

  useEffect(() => {
    if (!companyId) {
      return;
    }

    let isMounted = true;

    const syncCompanyTags = async () => {
      const allTags = await fetchAllCompanyTags(companyId);
      const initialTagState = buildTagState(allTags);
      const missingTagNames = getMissingTagNames(initialTagState);

      if (
        missingTagNames.length > 0
        && !syncingTagsRef.current
        && !syncedCompaniesRef.current.has(companyId)
      ) {
        syncingTagsRef.current = true;
        await Promise.allSettled(
          missingTagNames.map((tagName) =>
            postTagApi.post({
              name: tagName,
            }),
          ),
        );
        syncedCompaniesRef.current.add(companyId);
        syncingTagsRef.current = false;

        const refreshedTags = await fetchAllCompanyTags(companyId);
        const refreshedTagState = buildTagState(refreshedTags);

        if (!isMounted) {
          return;
        }

        setTagIds(refreshedTagState);
        setUserProfileTag(refreshedTagState[String(Tags.ProfilePicture)]);
        return;
      }

      if (!isMounted) {
        return;
      }

      setTagIds(initialTagState);
      setUserProfileTag(initialTagState[String(Tags.ProfilePicture)]);
    };

    syncCompanyTags();

    return () => {
      isMounted = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (companyId && Object.keys(tagIds).length > 0) {
      Object.values(tagIds).forEach(tagId => {
        fetchFilesApi.get(`/files?tag_id=${tagId}&company_id=${companyId}`, { tagId });
      });
    }
  }, [companyId, tagIds]);

  const uploadFileApi = useApi({
    path: '/files',
    defaultErrorMessage: 'File upload failed',
  });

  const deleteFileApi = useApi({
    path: '/file',
    defaultErrorMessage: 'File delete failed',
  });

  const updateFileApi = useApi({
    path: '/file',
    defaultErrorMessage: 'File update failed',
  });

  const onDropzoneFileUpload = (newFiles: Array<File>, tag: number | undefined) => {
    if (typeof tag !== 'number' || !companyId) {
      return;
    }

    // Files are staged locally so the user can fill in details before the
    // actual upload happens on Save — nothing is sent to the backend yet.
    const newPendingEntries = newFiles.map((file, index) => ({
      key: `pending-${Date.now()}-${index}-${file.name}`,
      file,
      expirationDate: '',
      notes: '',
    }));

    setPendingFiles(prev => ({
      ...prev,
      [tag]: [...(prev[tag] ?? []), ...newPendingEntries],
    }));
  }

  const onUpdatePendingFile = (tag: number, key: string, values: Partial<{ expirationDate: string; notes: string }>) => {
    setPendingFiles(prev => ({
      ...prev,
      [tag]: (prev[tag] ?? []).map((entry) => (entry.key === key ? { ...entry, ...values } : entry)),
    }));
  }

  const onDiscardPendingFile = (tag: number, key: string) => {
    setPendingFiles(prev => ({
      ...prev,
      [tag]: (prev[tag] ?? []).filter((entry) => entry.key !== key),
    }));
  }

  const onSavePendingFile = async (tag: number, key: string) => {
    if (!companyId) {
      return;
    }

    const pending = (pendingFiles[tag] ?? []).find((entry) => entry.key === key);
    if (!pending) {
      return;
    }

    setSavingPendingKey(key);

    const formData = new FormData();
    formData.append('file', pending.file);
    // Backend expects a single text field: one id ("24") or CSV ("24,31").
    formData.append('tag_ids', String(tag));
    formData.append('expiration_date', pending.expirationDate);
    formData.append('notes', pending.notes);

    try {
      await uploadFileApi.post(formData);
      await fetchFilesApi.get(`/files?tag_id=${tag}&company_id=${companyId}`, { tagId: tag });
      onDiscardPendingFile(tag, key);
    } finally {
      setSavingPendingKey(null);
    }
  }

  const onRequestDeleteFile = (fileId: number, tag: number | undefined) => {
    if (typeof tag !== 'number') {
      return;
    }
    setFileIdPendingDelete({ id: fileId, tag });
  }

  const onConfirmDeleteFile = () => {
    if (!fileIdPendingDelete || !companyId) {
      return;
    }
    const { id: fileId, tag } = fileIdPendingDelete;

    deleteFileApi.deleteApi(fileId).then(() => {
      fetchFilesApi.get(`/files?tag_id=${tag}&company_id=${companyId}`, { tagId: tag });
    });
    setFileIdPendingDelete(null);
  }

  const onStartEditFile = (file: { id: number; expirationDate: string; notes: string }) => {
    setEditingFileId(file.id);
    setEditValues({ expirationDate: file.expirationDate, notes: file.notes });
  }

  const onCancelEditFile = () => {
    setEditingFileId(null);
    setEditValues({ expirationDate: '', notes: '' });
  }

  const onSaveFile = (fileId: number, tag: number | undefined) => {
    if (!companyId || typeof tag !== 'number') {
      return;
    }

    updateFileApi.patch(fileId, {
      expiration_date: editValues.expirationDate,
      notes: editValues.notes,
    }).then(() => {
      setEditingFileId(null);
      fetchFilesApi.get(`/files?tag_id=${tag}&company_id=${companyId}`, { tagId: tag });
    });
  }

  const sidebarItems: SidebarItemProps[] = documentSections.map((section) => ({
    label: section.label,
    href: `#${section.id}`,
  }));

  return (
    <>
      <AccountTopBar />
      <div className="pd:mx-0 min-h-screen w-full bg-white px-5 md:flex lg:px-[190px]">
      <div className="flex-shrink-0 bg-white lg:min-w-[260px]">
        <Sidebar
          userName={`${localStorage.getItem('first_name')?.replace(/['"]+/g, '') ?? ''} ${localStorage.getItem('last_name')?.replace(/['"]+/g, '') ?? ''}`}
          items={sidebarItems}
          tagId={userProfileTag ?? 0}
        />
      </div>
      <div className="flex flex-1 flex-col items-start py-12 md:ml-16">
        <div className="w-full">
          <h1 className="mb-1 text-2xl font-bold">{t('documents')}</h1>
          <p>{t('documentsInfo')}</p>
          <div className="mt-4 md:hidden">
            <Listbox value={selectedSectionId} onChange={setSelectedSectionId}>
              <div className="relative">
                <Listbox.Button className="flex w-full items-center justify-between rounded border border-gray-300 bg-white px-4 py-3 text-left text-base font-semibold text-gray-900">
                  {documentSections.find((section) => section.id === selectedSectionId)?.label}
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="shrink-0"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </Listbox.Button>
                <Listbox.Options className="absolute z-10 mt-1 w-full overflow-hidden rounded border border-gray-200 bg-white shadow-lg focus:outline-none">
                  {documentSections.map((section) => (
                    <Listbox.Option
                      key={section.id}
                      value={section.id}
                      className={({ active }) =>
                        `cursor-pointer px-4 py-2 text-sm text-gray-900 ${active ? 'bg-gray-100' : ''}`
                      }
                    >
                      {section.label}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
          </div>
          {documentSections.map((section) => {
            return (
              <div
                key={section.id}
                className={`flex-col md:flex ${selectedSectionId === section.id ? 'flex' : 'hidden'}`}
              >
                <h2 id={section.id} className="mb-1 mt-10 text-xl font-bold">
                  {section.label}
                </h2>
                <div className="flex w-full flex-col border-b border-gray-200 py-4">
                  {section.documents.map((document) => {
                    const documentTagId = tagIds[document.tagName];
                    const documentFiles =
                      typeof documentTagId === 'number' ? uploadedFiles[documentTagId] ?? [] : [];

                    return (
                      <div
                        key={document.id}
                        className="grid w-full gap-4 border-b border-gray-100 py-4 md:grid-cols-[minmax(260px,1fr)_minmax(0,2fr)]"
                      >
                        <div className="flex flex-col">
                          <h3 className="text-lg font-semibold text-gray-900">{document.label}</h3>
                        </div>
                        <div className="w-full">
                          <div className="flex min-h-[110px] flex-col items-center justify-center rounded border border-gray-300 bg-[#fafbfc] p-4 shadow-sm">
                            <span className="mb-3 block text-center text-sm text-gray-700">
                              Drag &amp; Drop, or click Upload to select files
                            </span>
                            <DropzoneWrapper onFileUpload={onDropzoneFileUpload} tag={documentTagId} />
                          </div>
                          <ul className="mt-3 w-full">
                            {typeof documentTagId === 'number' && (pendingFiles[documentTagId] ?? []).map((entry) => {
                              const isSaving = savingPendingKey === entry.key;

                              return (
                                <li key={entry.key} className="mb-2 flex flex-col gap-2 rounded border border-blue-200 bg-blue-50/40 p-2 text-sm text-gray-800">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="italic text-gray-700 break-all">{entry.file.name}</span>
                                    <div className="flex shrink-0 flex-wrap gap-2">
                                      <button
                                        type="button"
                                        className="rounded border border-green-300 px-2 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60"
                                        onClick={() => onSavePendingFile(documentTagId, entry.key)}
                                        disabled={isSaving}
                                      >
                                        {isSaving ? 'Saving…' : 'Save'}
                                      </button>
                                      <button
                                        type="button"
                                        className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                                        onClick={() => onDiscardPendingFile(documentTagId, entry.key)}
                                        disabled={isSaving}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <label className="flex items-center gap-2">
                                      <span className="text-xs font-medium text-gray-600">Expiration date</span>
                                      <input
                                        type="date"
                                        className="rounded border border-gray-300 px-2 py-1 text-xs"
                                        value={entry.expirationDate}
                                        onChange={(e) => onUpdatePendingFile(documentTagId, entry.key, { expirationDate: e.target.value })}
                                      />
                                    </label>
                                    <label className="flex flex-1 items-center gap-2">
                                      <span className="text-xs font-medium text-gray-600">Notes</span>
                                      <input
                                        type="text"
                                        className="w-full flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
                                        value={entry.notes}
                                        onChange={(e) => onUpdatePendingFile(documentTagId, entry.key, { notes: e.target.value })}
                                      />
                                    </label>
                                  </div>
                                </li>
                              );
                            })}
                            {documentFiles.map((file) => {
                              const isEditing = editingFileId === file.id;

                              return (
                                <li key={file.id} className="mb-2 flex flex-col gap-2 rounded border border-gray-100 p-2 text-sm text-gray-800">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <a href={buildAuthenticatedFileUrl(file.url)} target="_blank" rel="noopener noreferrer" className="underline break-all">
                                      {file.name}
                                    </a>
                                    <div className="flex shrink-0 flex-wrap gap-2">
                                      {isEditing ? (
                                        <>
                                          <button
                                            type="button"
                                            className="rounded border border-green-300 px-2 py-1 text-xs font-semibold text-green-700 hover:bg-green-50"
                                            onClick={() => onSaveFile(file.id, documentTagId)}
                                          >
                                            Save
                                          </button>
                                          <button
                                            type="button"
                                            className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                                            onClick={onCancelEditFile}
                                          >
                                            Cancel
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          type="button"
                                          className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                                          onClick={() => onStartEditFile(file)}
                                        >
                                          Edit
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="rounded border border-red-300 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
                                        onClick={() => onRequestDeleteFile(file.id, documentTagId)}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                  {isEditing ? (
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                      <label className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-gray-600">Expiration date</span>
                                        <input
                                          type="date"
                                          className="rounded border border-gray-300 px-2 py-1 text-xs"
                                          value={editValues.expirationDate}
                                          onChange={(e) => setEditValues((prev) => ({ ...prev, expirationDate: e.target.value }))}
                                        />
                                      </label>
                                      <label className="flex flex-1 items-center gap-2">
                                        <span className="text-xs font-medium text-gray-600">Notes</span>
                                        <input
                                          type="text"
                                          className="w-full flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
                                          value={editValues.notes}
                                          onChange={(e) => setEditValues((prev) => ({ ...prev, notes: e.target.value }))}
                                        />
                                      </label>
                                    </div>
                                  ) : (
                                    (file.expirationDate || file.notes) && (
                                      <div className="flex flex-col gap-0.5 text-xs text-gray-500 sm:flex-row sm:gap-4">
                                        {file.expirationDate && <span>Expires: {file.expirationDate}</span>}
                                        {file.notes && <span>Notes: {file.notes}</span>}
                                      </div>
                                    )
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </div>
      <ConfirmDialog
        open={fileIdPendingDelete !== null}
        title="Delete file"
        message="Are you sure you want to delete this file? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={onConfirmDeleteFile}
        onCancel={() => setFileIdPendingDelete(null)}
      />
    </>
  )
}